// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyClientMembership } from "@/types";
import { pressToOpen, readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import "../row.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { membership: TyClientMembership; onOpen: () => void };

/**
 * One company a person works in, and what they are in it. The whole row opens that company, which is
 * the only thing it can do: **what somebody is inside a company is that company's business**, set by
 * its owner on its own team screen, so this card reports the role and never offers to change it.
 */
export default function MembershipCard({ membership, onOpen }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const { organization, role } = membership;
    const name = readLangValue(organization.name, lang) || organization.slug;

    return (
        <article className="row-card row-card--open" role="button" tabIndex={0} onClick={onOpen} onKeyDown={pressToOpen(onOpen)}>
            <Avatar name={name} picture={organization.logo} size={42} rounded bordered />

            <div className="row-card__body">
                <span className="row-card__title">{name}</span>
                <span className="row-card__meta row-card__ltr">{organization.slug}</span>
            </div>

            <div className="row-card__chips">
                {/* An owner is not a role in a list of roles — it is the company's one owner. */}
                <StatusChip size="sm" tone={role === "owner" ? "accent" : "neutral"} label={t.roles[role]} />
            </div>

            <span className="membership-card__go">
                <Icon name="chevron" size={16} />
            </span>
        </article>
    );
}
